import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Calendar, Clock, DollarSign, AlertTriangle, XCircle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function BookingManagement() {
  const { user } = useContext(AuthContext);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings/my-bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const calculateTimeToCheckIn = (checkInDate) => {
    const now = new Date();
    const checkIn = new Date(checkInDate);
    const timeDiff = checkIn.getTime() - now.getTime();
    const hoursRemaining = Math.ceil(timeDiff / (1000 * 3600));
    
    return {
      hoursRemaining,
      canCancel: hoursRemaining >= 24,
      timeString: hoursRemaining > 0 ? `${hoursRemaining} hours until check-in` : 'Check-in time passed'
    };
  };

  const openCancelModal = (booking) => {
    setSelectedBooking(booking);
    setCancelReason('');
    setShowCancelModal(true);
  };

  const closeCancelModal = () => {
    setShowCancelModal(false);
    setSelectedBooking(null);
    setCancelReason('');
  };

  const handleCancelBooking = async () => {
    if (!selectedBooking) return;
    
    setCancelling(selectedBooking.id);
    
    try {
      const response = await axios.post(
        `${API}/bookings/${selectedBooking.id}/cancel`,
        { reason: cancelReason || null }
      );
      
      const result = response.data;
      
      toast.success(
        <div>
          <strong>Booking Cancelled Successfully</strong>
          <br />
          {result.refund_eligible 
            ? `Refund of $${result.refund_amount.toFixed(2)} will be processed`
            : 'No refund available for this cancellation'
          }
        </div>,
        { duration: 5000 }
      );
      
      // Refresh bookings
      await fetchBookings();
      closeCancelModal();
      
    } catch (error) {
      console.error('Cancellation failed:', error);
      const message = error.response?.data?.detail || 'Failed to cancel booking';
      toast.error(message);
    } finally {
      setCancelling(null);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px 0', minHeight: '60vh' }}>
        <div className="container">
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <div className="loading-spinner"></div>
            <p style={{ marginTop: '20px', color: '#718096' }}>Loading your bookings...</p>
          </div>
        </div>
      </div>
    );
  }

  const activeBookings = bookings.filter(b => b.status !== 'cancelled');
  const cancelledBookings = bookings.filter(b => b.status === 'cancelled');

  return (
    <div style={{ padding: '40px 0', minHeight: 'calc(100vh - 70px)', background: '#f7fafc' }}>
      <div className="container">
        <div style={{ marginBottom: '40px' }}>
          <h1 style={{ 
            fontSize: '2.25rem', 
            fontWeight: '700', 
            marginBottom: '16px',
            color: '#2d3748'
          }}>
            My Bookings
          </h1>
          <p style={{ color: '#718096', fontSize: '1.1rem' }}>
            Manage your current and past bookings
          </p>
        </div>

        {/* Cancellation Policy Notice */}
        <div style={{ 
          background: 'linear-gradient(135deg, #fef5e7, #fed7aa)',
          border: '1px solid #fb923c',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '32px'
        }}>
          <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
            <AlertTriangle size={24} color="#ea580c" style={{ flexShrink: 0, marginTop: '2px' }} />
            <div>
              <h3 style={{ 
                color: '#ea580c', 
                fontWeight: '600', 
                marginBottom: '12px',
                fontSize: '1.1rem'
              }}>
                📅 STRICT Cancellation Policy
              </h3>
              <div style={{ color: '#9a3412', fontSize: '0.95rem', lineHeight: '1.6' }}>
                <p style={{ marginBottom: '12px' }}>
                  <strong>24-Hour Rule:</strong> Cancellations must be submitted at least 24 hours before check-in time for ANY refund consideration.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div>
                    <p style={{ fontWeight: '600', marginBottom: '6px' }}>✅ More than 24 hours before check-in:</p>
                    <ul style={{ paddingLeft: '20px', margin: '0' }}>
                      <li>75% refund issued to traveler</li>
                      <li>25% inconvenience fee applies</li>
                    </ul>
                  </div>
                  <div>
                    <p style={{ fontWeight: '600', marginBottom: '6px' }}>❌ Less than 24 hours before check-in:</p>
                    <ul style={{ paddingLeft: '20px', margin: '0' }}>
                      <li><strong>COMPLETELY NON-REFUNDABLE</strong></li>
                      <li>Full amount charged (0% refund)</li>
                      <li>Protects luxury resort marketplace</li>
                    </ul>
                  </div>
                </div>
                <p style={{ marginTop: '12px', fontSize: '0.875rem', fontStyle: 'italic' }}>
                  <strong>Fee Distribution:</strong> 50% to resort owners, 50% to ZypeeTravel. The $100 resort preparation fee is NEVER refundable under any circumstances.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Active Bookings */}
        <div style={{ marginBottom: '40px' }}>
          <h2 style={{ 
            fontSize: '1.5rem', 
            fontWeight: '600', 
            marginBottom: '24px',
            color: '#2d3748'
          }}>
            Active Bookings ({activeBookings.length})
          </h2>
          
          {activeBookings.length === 0 ? (
            <div style={{ 
              background: 'white',
              borderRadius: '16px',
              padding: '60px',
              textAlign: 'center',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
            }}>
              <Calendar size={48} style={{ margin: '0 auto 16px', color: '#a0aec0' }} />
              <p style={{ color: '#718096', fontSize: '1.1rem' }}>No active bookings</p>
              <p style={{ color: '#a0aec0', fontSize: '0.9rem' }}>Your future bookings will appear here</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '20px' }}>
              {activeBookings.map((booking) => {
                const timeInfo = calculateTimeToCheckIn(booking.check_in_date);
                
                return (
                  <div key={booking.id} style={{
                    background: 'white',
                    borderRadius: '16px',
                    padding: '24px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '1fr auto', 
                      gap: '20px',
                      alignItems: 'start'
                    }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                          <h3 style={{ 
                            fontSize: '1.2rem',
                            fontWeight: '600',
                            color: '#2d3748',
                            margin: 0
                          }}>
                            Booking #{booking.id.slice(-8)}
                          </h3>
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            background: booking.status === 'confirmed' ? '#d1fae5' : '#fef3c7',
                            color: booking.status === 'confirmed' ? '#065f46' : '#92400e'
                          }}>
                            {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                          </span>
                        </div>
                        
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                          gap: '16px',
                          marginBottom: '16px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Calendar size={16} color="#667eea" />
                            <div>
                              <p style={{ fontSize: '0.875rem', color: '#4a5568', margin: 0 }}>Check-in</p>
                              <p style={{ fontWeight: '600', color: '#2d3748', margin: 0 }}>
                                {new Date(booking.check_in_date).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Calendar size={16} color="#667eea" />
                            <div>
                              <p style={{ fontSize: '0.875rem', color: '#4a5568', margin: 0 }}>Check-out</p>
                              <p style={{ fontWeight: '600', color: '#2d3748', margin: 0 }}>
                                {new Date(booking.check_out_date).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <DollarSign size={16} color="#667eea" />
                            <div>
                              <p style={{ fontSize: '0.875rem', color: '#4a5568', margin: 0 }}>Total Amount</p>
                              <p style={{ fontWeight: '600', color: '#2d3748', margin: 0 }}>
                                ${booking.total_amount.toFixed(2)}
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Cancellation Time Info */}
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px',
                          padding: '12px',
                          background: timeInfo.canCancel ? '#f0fdf4' : '#fef2f2',
                          borderRadius: '8px',
                          border: timeInfo.canCancel ? '1px solid #bbf7d0' : '1px solid #fecaca'
                        }}>
                          <Clock size={16} color={timeInfo.canCancel ? '#16a34a' : '#dc2626'} />
                          <span style={{ 
                            fontSize: '0.875rem',
                            color: timeInfo.canCancel ? '#15803d' : '#b91c1c',
                            fontWeight: '500'
                          }}>
                            {timeInfo.timeString}
                            {timeInfo.canCancel ? ' • Cancellation eligible for 75% refund' : ' • No refund available'}
                          </span>
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'end' }}>
                        {timeInfo.hoursRemaining > 0 && (
                          <button
                            onClick={() => openCancelModal(booking)}
                            className="btn btn-outline"
                            style={{
                              color: timeInfo.canCancel ? '#dc2626' : '#6b7280',
                              borderColor: timeInfo.canCancel ? '#dc2626' : '#d1d5db',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                          >
                            <XCircle size={16} />
                            Cancel Booking
                          </button>
                        )}
                        
                        {booking.rental_agreement_id && (
                          <button
                            className="btn btn-outline"
                            style={{
                              color: '#667eea',
                              borderColor: '#667eea',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                          >
                            📄 View Agreement
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Cancelled Bookings */}
        {cancelledBookings.length > 0 && (
          <div>
            <h2 style={{ 
              fontSize: '1.5rem', 
              fontWeight: '600', 
              marginBottom: '24px',
              color: '#2d3748'
            }}>
              Cancelled Bookings ({cancelledBookings.length})
            </h2>
            
            <div style={{ display: 'grid', gap: '16px' }}>
              {cancelledBookings.map((booking) => (
                <div key={booking.id} style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                  border: '1px solid #fed7d7',
                  opacity: 0.8
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h4 style={{ 
                        fontSize: '1rem',
                        fontWeight: '600',
                        color: '#4a5568',
                        margin: '0 0 8px 0'
                      }}>
                        Booking #{booking.id.slice(-8)} (Cancelled)
                      </h4>
                      <p style={{ fontSize: '0.875rem', color: '#718096', margin: 0 }}>
                        {new Date(booking.check_in_date).toLocaleDateString()} - {new Date(booking.check_out_date).toLocaleDateString()}
                      </p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        background: '#fee2e2',
                        color: '#b91c1c'
                      }}>
                        Cancelled
                      </span>
                      <p style={{ fontSize: '0.875rem', color: '#718096', margin: '4px 0 0 0' }}>
                        ${booking.total_amount.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Cancellation Modal */}
        {showCancelModal && selectedBooking && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto'
            }}>
              <h3 style={{ 
                fontSize: '1.5rem',
                fontWeight: '600',
                marginBottom: '20px',
                color: '#2d3748'
              }}>
                Cancel Booking
              </h3>
              
              <div style={{ marginBottom: '20px' }}>
                <p style={{ color: '#4a5568', marginBottom: '16px' }}>
                  <strong>Booking:</strong> #{selectedBooking.id.slice(-8)}
                </p>
                <p style={{ color: '#4a5568', marginBottom: '16px' }}>
                  <strong>Dates:</strong> {new Date(selectedBooking.check_in_date).toLocaleDateString()} - {new Date(selectedBooking.check_out_date).toLocaleDateString()}
                </p>
                
                {(() => {
                  const timeInfo = calculateTimeToCheckIn(selectedBooking.check_in_date);
                  const refundAmount = timeInfo.canCancel ? selectedBooking.total_amount * 0.75 : 0;
                  
                  return (
                    <div style={{
                      padding: '16px',
                      background: timeInfo.canCancel ? '#f0fdf4' : '#fef2f2',
                      borderRadius: '8px',
                      border: timeInfo.canCancel ? '1px solid #bbf7d0' : '1px solid #fecaca',
                      marginBottom: '20px'
                    }}>
                      <p style={{ 
                        fontWeight: '600',
                        color: timeInfo.canCancel ? '#15803d' : '#b91c1c',
                        marginBottom: '8px'
                      }}>
                        {timeInfo.canCancel ? '✅ Refund Eligible' : '❌ No Refund Available'}
                      </p>
                      <p style={{ 
                        fontSize: '0.875rem',
                        color: timeInfo.canCancel ? '#166534' : '#991b1b'
                      }}>
                        {timeInfo.canCancel 
                          ? `You will receive a 75% refund of $${refundAmount.toFixed(2)}. A 25% inconvenience fee ($${(selectedBooking.total_amount * 0.25).toFixed(2)}) will be charged.`
                          : 'STRICT POLICY: Cancellations within 24 hours of check-in are COMPLETELY NON-REFUNDABLE. Full amount will be charged.'
                        }
                      </p>
                    </div>
                  );
                })()}
              </div>
              
              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '500', 
                  color: '#4a5568' 
                }}>
                  Reason for Cancellation (Optional)
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Please let us know why you're cancelling..."
                  rows="4"
                  className="form-input"
                  style={{ width: '100%', resize: 'vertical' }}
                />
              </div>
              
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'end' }}>
                <button
                  onClick={closeCancelModal}
                  className="btn btn-outline"
                  disabled={cancelling}
                >
                  Keep Booking
                </button>
                <button
                  onClick={handleCancelBooking}
                  className="btn btn-primary"
                  disabled={cancelling}
                  style={{
                    background: '#dc2626',
                    borderColor: '#dc2626',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {cancelling ? (
                    <>
                      <div className="loading-spinner" style={{ width: '16px', height: '16px' }}></div>
                      Cancelling...
                    </>
                  ) : (
                    <>
                      <XCircle size={16} />
                      Confirm Cancellation
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default BookingManagement;